import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { setTrue } from "../../store/slices/IdSlice";
import { closeAddModelData } from "../../store/slices/modalSlice";
import { Button, Form } from "react-bootstrap";
import "./model.css";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { fetchBusinessForm, submitBusinessForm } from "../../lib/api";
import PreLoader from "../preloader/PreLoader";

const Model = () => {
  const dispatch = useDispatch();
  const addModalOpen = useSelector( (state: { modal: { addModelData: boolean } }) => state.modal.addModelData );

  const handleCloseAddModal = () => {
    dispatch(setTrue());
    dispatch(closeAddModelData());
  };
  const pageUrl = window.location.href;

  // Dynamic DB data
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [step, setStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serviceName, setServiceName] = useState<string>("");
  const [serviceId, setServiceId] = useState(0);
  const formRef = useRef<HTMLDivElement | null>(null);

  const [formDetails, setFormDetails] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBusinessForm();
        setServiceName(data.serviceName);
        setServiceId(data.serviceId);
        setQuestions(data.questions || []);
        setTotalSteps((data.questions || []).length);
      } catch (err) {
        console.error("Failed to load questions", err);
      }
    })();
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "phone") {
      const numericVal = value.replace(/\D/g, "");
      setFormDetails(prev => ({ ...prev, phone: numericVal }));
    } else {
      setFormDetails(prev => ({ ...prev, [name]: value }));
    }
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const scrollToError = () => {
    setTimeout(() => {
      const el = formRef.current;
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const validateCurrentStep = (q: any, tempAnswers: any): boolean => {
    if (!q) return true;
    const newErrors: Record<string, string> = { ...errors };

    // Clear previous step-specific errors
    delete newErrors[q.questionId];
    const val = tempAnswers[q.questionId];
    
    switch (q.questionType) {
      case "number": {
        if (val === undefined || val === null || val === "") {
          newErrors[String(q.questionId)] = `${q.questionText} is required`;
        } else if (isNaN(Number(val))) {
          newErrors[String(q.questionId)] = `${q.questionText} must be a number`;
        }
        break;
      }
      case "dropdown": {
        if (!val) {
          newErrors[String(q.questionId)] = `Please select ${q.questionText}`;
        }
        break;
      }
      case "yes_no": {
        if (!val) {
          newErrors[String(q.questionId)] = `Please choose an option for ${q.questionText}`;
        }
        break;
      }
      case "form": {
        // Validate local contact form
        if (!formDetails.fullName.trim()) {
          newErrors["fullName"] = "Full Name is required";
        } else {
          newErrors["fullName"] = "";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formDetails.email.trim()) {
          newErrors["email"] = "Email is required";
        } else if (!emailRegex.test(formDetails.email)) {
          newErrors["email"] = "Enter a valid email address";
        } else {
          newErrors["email"] = "";
        }

        // Phone: enforce digits-only 10-digit Indian OR UAE 7-10? you requested digits only and 10-digit India
        // We'll implement: 10-digit India (starts 6-9) OR UAE 9-10 starting with mobile prefixes (best effort)
        const phone = formDetails.phone || "";
        const indiaRegex = /^[6-9]\d{9}$/; // 10-digit India
        const uaeRegex = /^[0-9]{7,10}$/; // len 7-10 general (you can tighten)
        if (!phone) {
          newErrors["phone"] = "Phone number is required";
        } else if (!indiaRegex.test(phone) && !uaeRegex.test(phone)) {
          newErrors["phone"] = "Enter a valid phone number (digits only)";
        } else {
          newErrors["phone"] = "";
        }

        // message optional - but add minimal length check if present
        if (formDetails.message && formDetails.message.length > 0 && formDetails.message.length < 3) {
          newErrors["message"] = "Message too short";
        } else {
          newErrors["message"] = "";
        }
        break;
      }
      default:
        break;
    }

    setErrors(newErrors);

    // if any newErrors entries for this step present -> scroll and return false
    const keyList = Object.keys(newErrors);
    const stepHasError = keyList.some(k => k === String(q.questionId) || ["fullName", "email", "phone", "message"].includes(k) && q.questionType === "form" && newErrors[k]);

    if (stepHasError) {
      scrollToError();
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep(currentQuestion, answers)) return;
    setStep(s => Math.min(s + 1, Math.max(0, totalSteps - 1)));
  };

  const handlePrev = () => {
    setStep(s => Math.max(s - 1, 0));
  };

  const handleAnswer = (questionId: number, value: any) => {
    const q = questions[step];
    const updatedAnswers = { ...answers, [questionId]: value };

    setAnswers(updatedAnswers);
    if (validateCurrentStep(q, updatedAnswers)) {
      setStep(s => Math.min(s + 1, totalSteps - 1));
    }
  };

  const handleSubmit = async () => {
    const q = questions[step];
    if (!validateCurrentStep(q, answers)) return;


    const enquiryPayload = questions.map(q => ({
      questionId: q.questionId,
      questionText: q.questionText,
      answer: answers[q.questionId] ?? ""
    }));

    enquiryPayload.push(
      { questionId: 9999, questionText: "Service Name", answer: serviceName },
      { questionId: 9998, questionText: "Page URL", answer: pageUrl }
    );

    try {  
      const postData = JSON.stringify({
        name: formDetails.fullName,
        email: formDetails.email,
        phone: formDetails.phone,
        message: formDetails.message,
        serviceName: serviceName,
        serviceId: serviceId || 0,
        pageUrl: pageUrl,
        answers: enquiryPayload
      })
      const res = await submitBusinessForm(postData);;
      if (res?.data.success) {
        // go to success step (last)
        setStep(totalSteps - 1);
      } else {
        console.error("Save failed", res?.data);
        alert("Failed to submit enquiry. Try again.");
      }
    } catch (err) {
      console.error("Submission error", err);
      alert("Submission error. Check console.");
    }
  };
  
  if(totalSteps == 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100dvh" }}>
        <PreLoader />
      </div>
    ); 
  }

  const currentQuestion = questions[step];

  return (
    <Modal show={addModalOpen} onHide={handleCloseAddModal} scrollable backdrop="static" centered className="mb-fix slide-from-bottom">
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        {step < totalSteps && (
          <div className="bg-2 py-3 rounded-3 mb-3">
            <h4 className="ff-bold font-22 text-primary-custom w-100 text-center">Calculate Your UAE Business Setup Cost</h4>
            {/* progress bar */}
            <div className="Bs-and-progress-container">
              <div className="Bs-and-progress-bar-fill" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
            </div>
          </div>
        )}

        <div className="Bs-and-question-card ">
          {currentQuestion?.questionType === "success" && step === totalSteps - 1 ? (

            <div className="Bs-and-estimate text-center py-4 pt-0 px-3">
              {/* Animated check / success lottie */}
              <div className="mb-3">
                <DotLottieReact
                  src="https://lottie.host/d0ef3a6b-81e6-44a8-bea8-efb3c9ec907c/mXD2J41Kgw.lottie"
                  autoplay
                />
              </div>
              {/* Heading */}
              <h4 className="ff-bold font-24 text-dark mb-2">
                {currentQuestion.questionText}
              </h4>

              {/* Description */}
              <p
                className="text-muted font-14 mx-auto"
                style={{ maxWidth: "500px" }}
              >
                {currentQuestion.questionDescription}
              </p>
            </div>
          ) : (
            <>
              {currentQuestion && (
                  <>
                    <h5 className="ff-bold font-18 mb-3"><i className={`${currentQuestion.iconClass} me-2`} />{currentQuestion.questionText}</h5>
                    {currentQuestion.questionDescription && <p className="text-muted font-12">{currentQuestion.questionDescription}</p>}
                  </>
              )}

              {currentQuestion?.questionType === "number" && (
                <>
                  <div className="Bs-and-options-grid">
                    {currentQuestion.options.map((opt: any) => (
                    <button
                      key={opt.optionValue}
                      className={`Bs-and-option-btn ${answers[currentQuestion.questionId] == opt.optionValue ? "active" : ""}`}
                      onClick={() => handleAnswer(currentQuestion.questionId, opt.optionValue)}>
                        {opt.optionLabel}
                      </button>
                    ))}
                  </div>
                  {errors[String(currentQuestion.questionId)] && <div className="error-text">{errors[String(currentQuestion.questionId)]}</div>}
                </>
              )}
              {currentQuestion.questionType === "dropdown" && (
                <>
                  <Form.Select className="form-select" value={answers[currentQuestion.questionId] || ""} onChange={(e) => handleAnswer(currentQuestion.questionId, e.target.value)}>
                    <option value="">Choose...</option>
                    {currentQuestion.options.map((opt: any) => (<option key={opt.optionValue} value={opt.optionValue}>{opt.optionLabel}</option>))}
                  </Form.Select>
                  {errors[String(currentQuestion.questionId)] && <div className="error-text">{errors[String(currentQuestion.questionId)]}</div>}
                </>
              )}
              {currentQuestion.questionType === "yes_no" && (
                <>
                  <div className="Bs-and-options-grid">
                    {currentQuestion.options.map((opt: any) => (
                      <button key={opt.optionValue} className={`Bs-and-option-btn ${answers[currentQuestion.questionId] === opt.optionValue ? "active" : ""}`} onClick={() => handleAnswer(currentQuestion.questionId, opt.optionValue)}>
                        {opt.optionLabel}
                      </button>
                    ))}
                  </div>
                  {errors[String(currentQuestion.questionId)] && <div className="error-text">{errors[String(currentQuestion.questionId)]}</div>}
                </>
              )}
              {currentQuestion.questionType === "form" && (
                <>
                  <Form className="up-form">
                    <Form.Group className="mb-3 form-group">
                      <Form.Control type="text" id="fullName" name="fullName" value={formDetails.fullName} onChange={handleFormChange} className="form-control py-3" placeholder="Full Name" required />
                      <Form.Label htmlFor="fullName">Full Name</Form.Label>
                      {errors.fullName && <div className="error-text">{errors.fullName}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3 form-group">
                      <Form.Control type="email" id="email" name="email" value={formDetails.email} onChange={handleFormChange} placeholder="Email" className="form-control py-3" required />
                      <Form.Label htmlFor="email">Email</Form.Label>
                      {errors.email && <div className="error-text">{errors.email}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3 form-group">
                      <Form.Control type="text" id="phone" name="phone" value={formDetails.phone} onChange={handleFormChange} placeholder="Phone Number" className="form-control py-3" required />
                      <Form.Label htmlFor="phone">Phone Number</Form.Label>
                      {errors.phone && <div className="error-text">{errors.phone}</div>}
                    </Form.Group>
                    
                    <Form.Group className="mb-3 form-group">
                      <Form.Control as="textarea" rows={3} id="message" name="message" value={formDetails.message} placeholder="Message" onChange={handleFormChange} className="form-control py-3" />
                      {errors.message && <div className="error-text">{errors.message}</div>}
                    </Form.Group>
                  </Form>
                </>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="Bs-and-nav-footer justify-content-between">
        {step > 0 && step < totalSteps - 2 && <button className="btn Bs-and-btn-nav prev1" onClick={handlePrev}>Previous</button>}
         {currentQuestion && currentQuestion.questionType !== "form" && step < totalSteps - 2 && (
          <button className="btn Bs-and-btn-nav next ms-auto" onClick={handleNext}>Next</button>
        )}
        {currentQuestion && currentQuestion.questionType === "form" && step === totalSteps - 2 && (
          <Button className="Bs-and-btn-submit w-100 py-3" onClick={handleSubmit}>SUBMIT</Button>
        )}
        {step === totalSteps - 1 && (
          <Button className="Bs-and-btn-submit w-100 py-3" onClick={handleCloseAddModal}>Okay got it</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default Model;
